import { apiService } from './api';
import type { 
  Game, 
  GameVersion, 
  GameFilters, 
  ApiResponse, 
  VersionComparison 
} from '../types/api';

export class ProjectsService {
  
  /**
   * Map raw API project data to Game type
   * Ensures consistent data structure across all service methods
   */
  private mapProjectData(project: any): Game {
    return {
      id: project.id,
      name: project.content?.name || project.project_slug || 'Unnamed Project',
      description: project.content?.description || project.content?.short_description || 'No description available',
      project_slug: project.project_slug,
      team_id: project.team_id,
      status: project.status || 'active',
      created_at: project.created_at,
      updated_at: project.updated_at,
      owner_id: project.user_id,
      latest_version: project.currentVersion?.version_number || 'No version',
      team: project.team,
      currentVersion: project.currentVersion,
      content: project.content,
      stats: project.stats,
      ownership_type: project.ownership_type,
      user_role: project.user_role,
      total_downloads: project.total_downloads,
      total_plays: project.total_plays,
      total_likes: project.total_likes,
      total_favorites: project.total_favorites,
      total_avg_viewers: project.total_avg_viewers,
      storage_path: project.storage_path,
      current_version_id: project.current_version_id,
      current_bundle_version: project.current_bundle_version,
      is_editable_in_studio: project.is_editable_in_studio,
      last_edit: project.last_edit,
      current_version_comment: project.current_version_comment,
      owner_display_name: project.owner_display_name,
      owner_avatar: project.owner_avatar,
      archived_at: project.archived_at,
    };
  }

  /**
   * Get all projects for the authenticated user
   * Uses: GET /api/studio/games (Studio-specific endpoint)
   */
  async getUserProjects(filters?: GameFilters): Promise<Game[]> {
    console.log('🎮 Getting user projects with filters:', filters);
    
    try {
      const response = await apiService.get('/studio/games', {
        params: filters,
      });
      
      console.log('🔍 Raw API response:', response.data);
      
      // Handle both API response formats
      let rawProjects;
      if (response.data && response.data.success && response.data.data) {
        // Standard format: {success: true, data: [...]}
        rawProjects = response.data.data;
        console.log('🔍 Using standard API format');
      } else if (response.data && typeof response.data === 'object') {
        // Object with numeric keys format: {"0": {...}, "1": {...}}
        rawProjects = Object.values(response.data);
        console.log('🔍 Using numeric keys format');
      } else {
        rawProjects = [];
      }
      
      console.log('🔍 Raw projects data:', rawProjects.length, 'projects');
      console.log('🔍 Sample raw project with stats:', rawProjects[0]);
      if (rawProjects[0]) {
        console.log('🔍 Stats check:', {
          total_plays: rawProjects[0].total_plays,
          total_avg_viewers: rawProjects[0].total_avg_viewers,
          total_favorites: rawProjects[0].total_favorites,
          total_likes: rawProjects[0].total_likes,
          total_downloads: rawProjects[0].total_downloads
        });
      }
      
      // Use consistent mapping helper
      const projects = rawProjects.map((project: any) => {
        const mappedProject = this.mapProjectData(project);
        
        console.log('🔍 Mapped project:', {
          id: mappedProject.id,
          project_slug: mappedProject.project_slug,
          name: mappedProject.name,
          raw_project_id: project.id,
          raw_project_slug: project.project_slug
        });
        
        return mappedProject;
      });
      
      console.log('✅ Projects transformed:', projects.length, 'projects');
      return projects;
    } catch (error) {
      console.error('❌ Failed to fetch projects:', error);
      throw error;
    }
  }

  /**
   * Search projects
   * Uses: GET /api/studio/games (with search filter)
   */
  async searchProjects(query: string, filters?: GameFilters): Promise<Game[]> {
    console.log('🔍 Searching projects with query:', query);
    
    try {
      const response = await apiService.get('/studio/games', {
        params: { search: query, ...filters },
      });
      
      // Handle both API response formats
      let rawProjects;
      if (response.data && response.data.success && response.data.data) {
        // Standard format: {success: true, data: [...]}
        rawProjects = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Object with numeric keys format: {"0": {...}, "1": {...}}
        rawProjects = Object.values(response.data);
      } else {
        rawProjects = [];
      }
      
      // Use consistent mapping helper
      const projects = rawProjects
          .map((project: any) => this.mapProjectData(project))
          .filter((project: Game) => 
            project.name.toLowerCase().includes(query.toLowerCase()) ||
            (project.description || '').toLowerCase().includes(query.toLowerCase()) ||
            project.project_slug.toLowerCase().includes(query.toLowerCase())
          );
        
        console.log('✅ Search results:', projects.length, 'projects found');
        return projects;
    } catch (error) {
      console.error('❌ Failed to search projects:', error);
      throw error;
    }
  }

  /**
   * Get project details by ID
   * Uses: GET /api/studio/games (filtered by ID)
   */
  async getProjectDetails(projectId: string): Promise<Game> {
    console.log('📋 Getting project details for:', projectId);
    
    try {
      // For now, get all games and filter by ID
      const response = await apiService.get<ApiResponse<Game[]>>('/studio/games');
      
      if (response.data.success && response.data.data) {
        // Studio only uses UUIDs - look up by id only
        const project = response.data.data.find((game: any) => 
          game.id === projectId
        );
        if (project) {
          // Use consistent mapping helper to ensure proper data structure
          const mappedProject = this.mapProjectData(project);
          console.log('✅ Project details fetched:', mappedProject.name);
          return mappedProject;
        } else {
          throw new Error('Project not found');
        }
      } else {
        throw new Error('Failed to fetch projects');
      }
    } catch (error) {
      console.error('❌ Failed to fetch project details:', error);
      throw error;
    }
  }

  /**
   * Get project versions
   * Uses: GET /api/studio/games/:projectId/versions
   */
  async getProjectVersions(projectId: string, options?: {
    include_files?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<GameVersion[]> {
    console.log('📦 Getting versions for project:', projectId);
    
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', options.limit.toString());
      if (options?.offset) params.set('page', Math.floor((options.offset || 0) / (options.limit || 20) + 1).toString());
      
      const response = await apiService.get<ApiResponse<GameVersion[]>>(
        `/studio/games/${projectId}/versions?${params.toString()}`
      );
      
      if (response.data.success && response.data.data) {
        console.log('✅ Versions fetched:', response.data.data.length);
        return response.data.data;
      } else {
        throw new Error('Failed to fetch versions');
      }
    } catch (error) {
      console.error('❌ Failed to fetch versions:', error);
      throw error;
    }
  }

  /**
   * Get specific version details
   * Uses: GET /api/studio/games/:projectId/versions/:versionId
   */
  async getVersionDetails(projectId: string, versionId: string): Promise<GameVersion> {
    console.log('📄 Getting version details:', versionId, 'for project:', projectId);
    
    try {
      const response = await apiService.get<ApiResponse<GameVersion>>(
        `/studio/games/${projectId}/versions/${versionId}`
      );
      
      if (response.data.success && response.data.data) {
        console.log('✅ Version details fetched:', response.data.data.version_number);
        return response.data.data;
      } else {
        throw new Error('Version not found');
      }
    } catch (error) {
      console.error('❌ Failed to fetch version details:', error);
      throw error;
    }
  }

  /**
   * Delete a project
   * Uses: DELETE /api/studio/games/:projectId
   */
  async deleteProject(projectId: string): Promise<boolean> {
    console.log('🗑️ Deleting project:', projectId);
    
    try {
      const response = await apiService.delete<ApiResponse>(`/studio/games/${projectId}`);
      
      if (response.data.success) {
        console.log('✅ Project deleted successfully');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete project');
      }
    } catch (error) {
      console.error('❌ Failed to delete project:', error);
      throw error;
    }
  }

  /**
   * Create a new project
   * Uses: POST /api/studio/games
   */
  async createProject(data: {
    name?: string;
    template?: string | null;
    prompt?: string | null;
    style?: string;
    interactivity?: string[];
  }): Promise<Game> {
    console.log('✨ Creating new project with:', data);
    
    try {
      const response = await apiService.post<ApiResponse<{ game: Game }>>('/studio/games', data);
      
      if (response.data.success && response.data.data?.game) {
        console.log('✅ Project created successfully:', response.data.data.game.id);
        return response.data.data.game;
      } else {
        throw new Error(response.data.message || 'Failed to create project');
      }
    } catch (error) {
      console.error('❌ Failed to create project:', error);
      throw error;
    }
  }

  /**
   * Duplicate a project
   * Uses: POST /api/studio/games/:projectId/duplicate
   */
  async duplicateProject(projectId: string, newName?: string): Promise<Game> {
    console.log('🔄 Duplicating project:', projectId, 'with name:', newName);
    
    try {
      const response = await apiService.post<ApiResponse<Game>>(`/studio/games/${projectId}/duplicate`, {
        name: newName
      });
      
      if (response.data.success && response.data.data) {
        console.log('✅ Project duplicated successfully:', response.data.data.name);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to duplicate project');
      }
    } catch (error) {
      console.error('❌ Failed to duplicate project:', error);
      throw error;
    }
  }

  /**
   * Export a project
   * Uses: GET /api/studio/games/:projectId/export
   */
  async exportProject(projectId: string): Promise<void> {
    console.log('📤 Exporting project:', projectId);
    
    try {
      // Create a download link that will trigger the export
      const response = await apiService.get(`/studio/games/${projectId}/export`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectId}_export_${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Project export initiated successfully');
    } catch (error) {
      console.error('❌ Failed to export project:', error);
      throw error;
    }
  }

  /**
   * Rollback to a specific version
   * Uses: POST /api/studio/games/:projectId/versions/:versionId/rollback
   */
  async rollbackToVersion(projectId: string, versionId: string): Promise<boolean> {
    console.log('⏪ Rolling back project:', projectId, 'to version:', versionId);
    
    try {
      const response = await apiService.post<ApiResponse>(
        `/studio/games/${projectId}/versions/${versionId}/rollback`
      );
      
      if (response.data.success) {
        console.log('✅ Rollback successful');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to rollback');
      }
    } catch (error) {
      console.error('❌ Failed to rollback:', error);
      throw error;
    }
  }

  /**
   * Update project metadata and content
   * Uses: PUT /api/studio/games/:projectId/content for content updates
   * Uses: PUT /api/studio/games/:projectId/status for status updates
   */
  async updateProject(projectId: string, data: {
    name?: string;
    description?: string;
    status?: string;
    category?: string;
    tags?: string[];
    icon_url?: string;
    cover_image_url?: string;
    play_instructions?: string;
    screenshot_urls?: string[];
    age_rating?: string;
    content_warnings?: string[];
    seo_keywords?: string[];
  }): Promise<Game> {
    console.log('✏️ Updating project:', projectId, 'with data:', data);
    
    try {
      let updatedProject: Game | null = null;

      // Handle status updates separately
      if (data.status) {
        console.log('🔄 Updating project status:', data.status);
        
        // Convert UI status values to API status values
        let apiStatus: string;
        switch (data.status) {
          case 'published':
            apiStatus = 'active';
            break;
          case 'draft':
            apiStatus = 'inactive';
            break;
          case 'archived':
            apiStatus = 'inactive';
            break;
          default:
            // If it's already an API status ('active', 'inactive', 'deleted'), keep it
            apiStatus = data.status;
            break;
        }

        const statusResult = await apiService.put<ApiResponse<Game>>(
          `/studio/games/${projectId}/status`,
          { status: apiStatus }
        );

        if (statusResult.data.success && statusResult.data.data) {
          updatedProject = statusResult.data.data;
          console.log('✅ Project status updated successfully');
        }
      }

      // Handle content updates (including images, instructions, screenshots)
      const contentFields = {
        name: data.name,
        description: data.description,
        category: data.category,
        tags: data.tags,
        play_instructions: data.play_instructions,
        screenshot_urls: data.screenshot_urls,
        age_rating: data.age_rating,
        content_warnings: data.content_warnings,
        seo_keywords: data.seo_keywords,
        icon_url: data.icon_url,
        cover_image_url: data.cover_image_url
      };

      // Remove undefined values
      const contentData = Object.fromEntries(
        Object.entries(contentFields).filter(([_, value]) => value !== undefined)
      );

      if (Object.keys(contentData).length > 0) {
        console.log('🔄 Updating project content:', contentData);
        
        const contentResult = await apiService.put<ApiResponse<Game>>(
          `/studio/games/${projectId}/content`,
          contentData
        );

        if (contentResult.data.success && contentResult.data.data) {
          updatedProject = contentResult.data.data;
          console.log('✅ Project content updated successfully');
        }
      }

      if (!updatedProject) {
        throw new Error('No valid updates were processed');
      }

      console.log('✅ Project updated successfully');
      return updatedProject;
    } catch (error) {
      console.error('❌ Error updating project:', error);
      throw error;
    }
  }

  /**
   * Toggle project archive status
   * Uses: PUT /api/studio/games/:projectId/status
   */
  async togglePublishStatus(projectId: string, unpublish: boolean): Promise<Game> {
    console.log('📦 Toggling project publish status:', projectId, 'unpublish:', unpublish);
    
    try {
      const status = unpublish ? 'inactive' : 'active';
      
      const response = await apiService.put<ApiResponse<Game>>(
        `/studio/games/${projectId}/status`,
        { status }
      );
      
      console.log('✅ Project publish status updated');
      if (!response.data.data) {
        throw new Error('Invalid response data');
      }
      return response.data.data;
    } catch (error) {
      console.error('❌ Error toggling project publish status:', error);
      throw error;
    }
  }

  /**
   * Toggle game visibility (public/private)
   * Uses: PUT /api/studio/games/:projectId/content
   */
  async toggleVisibility(projectId: string, makePrivate: boolean): Promise<Game> {
    console.log('🔒 Toggling game visibility:', projectId, 'makePrivate:', makePrivate);
    
    try {
      const visibility = makePrivate ? 'private' : 'public';
      
      const response = await apiService.put<ApiResponse<Game>>(
        `/studio/games/${projectId}/content`,
        { visibility }
      );
      
      if (response.data.success && response.data.data) {
        console.log('✅ Game visibility updated successfully');
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update visibility');
      }
    } catch (error) {
      console.error('❌ Failed to toggle visibility:', error);
      throw error;
    }
  }

  /**
   * Get dashboard statistics
   * Custom endpoint that aggregates data from existing endpoints
   */
  async getDashboardStats(): Promise<{
    total_projects: number;
    total_versions: number;
    active_projects: number;
    archived_projects: number;
  }> {
    console.log('📊 Getting dashboard statistics...');
    
    try {
      // Get all projects to calculate stats
      const projects = await this.getUserProjects();
      
      const stats = {
        total_projects: projects.length,
        total_versions: projects.reduce((sum, p) => sum + (p.stats?.versionCount || 0), 0),
        active_projects: projects.filter(p => p.status === 'active').length,
        archived_projects: projects.filter(p => p.status === 'inactive').length,
      };
      
      console.log('✅ Dashboard stats calculated:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Failed to get dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Archive a specific version
   * Uses: PUT /api/studio/games/:projectId/versions/:versionId/archive
   */
  async archiveVersion(projectId: string, versionId: string): Promise<boolean> {
    console.log('📦 Archiving version:', versionId, 'for project:', projectId);
    
    try {
      const response = await apiService.put<ApiResponse>(
        `/studio/games/${projectId}/versions/${versionId}/archive`
      );
      
      if (response.data.success) {
        console.log('✅ Version archived successfully');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to archive version');
      }
    } catch (error) {
      console.error('❌ Failed to archive version:', error);
      throw error;
    }
  }

  /**
   * Delete a specific version
   * Uses: DELETE /api/studio/games/:projectId/versions/:versionId
   */
  async deleteVersion(projectId: string, versionId: string): Promise<boolean> {
    console.log('🗑️ Deleting version:', versionId, 'for project:', projectId);
    
    try {
      const response = await apiService.delete<ApiResponse>(
        `/studio/games/${projectId}/versions/${versionId}`
      );
      
      if (response.data.success) {
        console.log('✅ Version deleted successfully');
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete version');
      }
    } catch (error) {
      console.error('❌ Failed to delete version:', error);
      throw error;
    }
  }

  /**
   * Download a specific version
   * Uses: GET /api/studio/games/:projectId/versions/:versionId/download
   */
  async downloadVersion(projectId: string, versionId: string): Promise<void> {
    console.log('⬇️ Downloading version:', versionId, 'for project:', projectId);
    
    try {
      const response = await apiService.get(
        `/studio/games/${projectId}/versions/${versionId}/download`,
        {
          responseType: 'blob',
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `project-${projectId}-version-${versionId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Version download initiated');
    } catch (error) {
      console.error('❌ Failed to download version:', error);
      throw error;
    }
  }

  /**
   * Compare two versions
   * Uses: GET /api/studio/games/:projectId/versions/compare?versionA=:versionA&versionB=:versionB
   */
  async compareVersions(projectId: string, versionA: string, versionB: string): Promise<VersionComparison> {
    console.log('🔍 Comparing versions:', versionA, 'and', versionB, 'for project:', projectId);
    
    try {
      const response = await apiService.get<ApiResponse<VersionComparison>>(
        `/studio/games/${projectId}/versions/compare`,
        {
          params: {
            versionA,
            versionB
          }
        }
      );
      
      if (response.data.success && response.data.data) {
        console.log('✅ Version comparison completed');
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to compare versions');
      }
    } catch (error) {
      console.error('❌ Failed to compare versions:', error);
      throw error;
    }
  }

  /**
   * Get recent projects (last 10 created/updated)
   */
  async getRecentProjects(limit: number = 5): Promise<Game[]> {
    console.log('📅 Getting recent projects, limit:', limit);
    
    try {
      const filters: GameFilters = {
        sort: 'updated_at',
        order: 'desc',
        limit: limit,
      };
      
      return await this.getUserProjects(filters);
    } catch (error) {
      console.error('❌ Failed to get recent projects:', error);
      throw error;
    }
  }

  /**
   * Transfer project to another team
   * Uses: PUT /api/studio/games/:projectId/transfer
   */
  async transferProject(projectId: string, targetTeamId: string): Promise<Game> {
    console.log('🔄 Transferring project:', projectId, 'to team:', targetTeamId);
    
    try {
      const response = await apiService.put<ApiResponse<Game>>(
        `/studio/games/${projectId}/transfer`,
        { team_id: targetTeamId }
      );
      
      if (response.data.success && response.data.data) {
        console.log('✅ Project transferred successfully');
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to transfer project');
      }
    } catch (error) {
      console.error('❌ Failed to transfer project:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const projectsService = new ProjectsService(); 